import _ from 'lodash';
import classNames from 'classnames';
import React, { FunctionComponent, useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext, Draggable, DragStart, Droppable, DropResult } from 'react-beautiful-dnd';
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  FormCheck,
  Modal,
  Nav,
  OverlayTrigger,
  Popover,
  Row,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { ClipLoader } from 'react-spinners';
import { v4 as uuidv4 } from 'uuid';
import toastr from '../../../toastr';

// Context menu support
import { Item, Menu, MenuProvider } from 'react-contexify';
import 'react-contexify/dist/ReactContexify.min.css';
import modal from '../../typings/Modal';

import CustomerLookup from '../lookups/CustomerLookup';
import Label from '../Label';
import Model from '../../model/ShopKeeper.model';
import VendorLookup from '../lookups/VendorLookup';
import { IVendorSuggestions } from '../../typings/VendorSuggestions';
import {
  IBackorder, // Data structure used as confirmation/POs are built out
  IBackorderSubmission, // Finalized "merged" data structure for submission
  ICandidatePartDetail,
  IConfirmationDetail,
  IDerivedVendorSuggestion,
  IPODetail,
  IPOLocation,
  IPOPart,
  FinalizedPOPart,
  POPartLineAction,
} from '../../typings/Backorder';

/* Note the displayed UI jargon of "Invoice on Hand" is changed to "Issue on Hand"
   for PRO1/SMM3 screens.  However, the code will continue to reference "invoice"
   rather than "issue". It essentially means the same thing (Invoice/Issue whatever
   you have on hand).
*/

interface IProps {
  container: Object;
  hide: () => void;
  mode: 'NOW' | 'DAILY';
  model: Model;
  show: boolean;
}

const contextMenuEvent = isMobile ? 'onClick' : 'onContextMenu';

/* Bootstrap's modal system confuses react-beautiful-dnd positioning calculation
   when dragging. To dance around that problem, we'll use a react portal appended
   directly to the end of the body.  By doing so, the dragging fixed position
   calculation will work correctly in reference to the viewport coordinates.
*/
const portal = document.createElement('div');
portal.classList.add('react-beautiful-dnd-portal');
document.body.appendChild(portal);

// ck1979 represents a "not set" value (and is also an attribution to the orignal author ;))
const blank = 'ck1979';

const poTemplate: IPODetail = {
  dealerPO: 'NEW',
  dealershipInternalComments: '',
  directShipCustomerNum: '',
  directShipOtherAddress: '',
  directShipOtherAttention: '',
  directShipOtherCityStateZip: '',
  directShipOtherName: '',
  directShipOtherPhone: '',
  directShipType: 'D',
  directShipVendorNum: '',
  freightOnBoard: blank,
  freightPayment: blank,
  miscAddress: '',
  miscAttention: '',
  miscCityStateZip: '',
  miscPhone: '',
  miscVendorName: '',
  shipIn: blank,
  suggestedVendor: blank,
  vendorSearch: '',
};

// TODO: Seed confirmation details from model
const boTemplate: IBackorder = {
  confirmationDetail: {
    contactEmail: '',
    contactFax: '',
    contactName: '',
    contactPhone: '',
    customerPO: '',
    dailyOrderPriorityCode: blank,
    dailyOrderShippingCode: blank,
    freight: '',
    fuel: '',
    shipIn: blank,
    shipOut: blank,
    specialComments: '',
  },
  purchaseOrders: [
    {
      parts: [],
      poDetail: _.clone(poTemplate),
    },
  ],
};

// useful for quick demoing
// prettier-ignore
// const mockModelParts: ICandidatePartDetail[] = [
//   { invoiceIndex: 0, ns: false, partNum: 'X:2-1373', stockClass: '100', oh: _.random(0, 10), req: _.random(0, 10), desc: 'Lorem ipsum dolor sit amet' },
//   { invoiceIndex: 1, ns: false, partNum: 'X:DT-5', stockClass: '152', oh: _.random(0, 10), req: _.random(0, 10), desc: 'Lorem ipsum dolor sit amet' },
//   { invoiceIndex: 2, ns: false, partNum: 'K:198-8263', stockClass: '100', oh: _.random(0, 10), req: _.random(0, 10), desc: 'Lorem ipsum dolor sit amet' },
//   { invoiceIndex: 3, ns: false, partNum: 'X:55620', stockClass: '590', oh: 0, req: _.random(0, 10), desc: 'Lorem ipsum dolor sit amet' },
//   { invoiceIndex: 4, ns: false, partNum: 'C:3905403', stockClass: '100', oh: _.random(0, 10), req: _.random(0, 10), desc: 'Lorem ipsum dolor sit amet' },
// ];

// Reasonably assume everything until the first : is the vendor number.
// This enables showing the vendor name in the auto-suggest box seamlessly.
function extractVendorNum(value: string): string {
  const colonPos = value.indexOf(':');
  return ~colonPos ? value.substr(0, colonPos) : value;
}

// This inits a map which tracks which parts are currently "selected" pending on
// a multi-part drag.  The map will be often reset.  No need to make this a closure.
function initPartSelectionToggleMap(partList: string[]): { [id: string]: boolean } {
  const map = {};
  partList.forEach(id => (map[id] = false));
  return map;
}

const defaultActionsPopover = (
  <Popover id="modal-popover-defaultactions">
    <Popover.Title>Default Actions</Popover.Title>
    <Popover.Content>
      Set the default action and pre-bill/pre-issue selections BEFORE adding parts to a PO. The default actions will
      apply to any item where it makes sense to do so. For example, if the default action is set to "Hold on Hand", but
      no on hand exists for a particular item, that item will be set to "Order Full Quantity". You may change these
      settings as you add more parts to POs. It will only apply to parts added after changing the settings. Existing
      parts already added to a PO will not be changed.
    </Popover.Content>
  </Popover>
);

const showAllPopover = (
  <Popover id="modal-popover-showall">
    <Popover.Title>Show All Parts</Popover.Title>
    <Popover.Content>
      This option shows ALL parts, including those with sufficient quantity to meet demand. This option is used in
      scenarios in which you wish to hold all parts on the screen regardless of quantity on hand. Additionally, this
      option shows any comment (memo) lines present on the main invoicing/issuing screen for quick addition to POs.
    </Popover.Content>
  </Popover>
);

const BackorderModal: FunctionComponent<IProps> = ({ container, hide, mode = 'NOW', model, show }) => {
  const isRO = ['PRO1', 'SMM3', 'SRE2'].includes(model.runMode);

  /**** STATE HOOKS ****/

  // Parts will only be extracted from the model once.  No need to use a memo hook.
  // The BO modal passes around partIDs only for performance, which is an index into
  // the resultant (static) parts state derived here.
  const [parts] = useState<ICandidatePartDetail[]>(() => {
    const extractedParts: ICandidatePartDetail[] = [];
    let nextIndex = 0;

    // A perusal of LineItem.tsx:render() will explain this model walk. Line item type test order matters.
    model.lineItems.forEach((lineItem, invoiceIndex) => {
      // Do not allow line items previously added from confirmations nor previously issued to a repair order
      if (
        !lineItem.fetched ||
        lineItem.confirmationSale ||
        lineItem.confirmationComment ||
        lineItem.issuedToRO ||
        lineItem.unpriced
      ) {
        return;
      }

      let sufficientQty = false;

      // Supercedence part?
      if (lineItem.supercedenceDetails && lineItem.supercedenceDetailsSupplemental && lineItem.detailSupplemental) {
        // 1:1 and 1:1:n supercedence chains supported, splits forthcoming
        if (lineItem.supercedenceDetails.length > 1) {
          console.error(`${lineItem.partNum} split supercedence not yet supported on backorders.`);
          return;
        }

        const supercedenceDetail = lineItem.supercedenceDetails[0];
        const supercedenceDetailSupplemental = _.get(
          lineItem.supercedenceDetailsSupplemental,
          `${supercedenceDetail.partNum}`
        );

        const req = supercedenceDetail.quantity; // this also accounts for 1:n quantity escalation, previously calculated on inquiry
        const oh = supercedenceDetail.priceSymbol.endsWith('3')
          ? req
          : Number.parseInt(_.get(supercedenceDetailSupplemental, 'onHand', ''), 10) || 0;
        const supercedenceEscalationFactor = supercedenceDetail.quantity / lineItem.quantity; // this will be a whole number by deduction

        if (oh >= req) {
          sufficientQty = true;
        }

        extractedParts[nextIndex++] = {
          desc: supercedenceDetail.shortDescription
            ? supercedenceDetail.shortDescription
            : supercedenceDetail.longDescription,
          invoiceIndex,
          ns: false,
          oh,
          partNum: supercedenceDetail.partNum,
          req,
          stockClass: supercedenceDetail.stockClass,
          sufficientQty,
          supercedenceEscalationFactor,
          // price forces do not exist on supercedence parts
        };

        // Regular stocking part?
      } else if (lineItem.detail) {
        const oh = lineItem.detail.priceSymbol.endsWith('3')
          ? lineItem.quantity
          : lineItem.detailSupplemental
          ? Number.parseInt(lineItem.detailSupplemental.onHand, 10) || 0
          : 0;
        const req = lineItem.quantity;

        // We only want candidate "stocking" parts if the on-hand is insufficient
        if (oh >= req) {
          sufficientQty = true;
        }

        extractedParts[nextIndex++] = {
          desc: lineItem.forcedDescription
            ? lineItem.forcedDescription
            : lineItem.detail.shortDescription
            ? lineItem.detail.shortDescription
            : lineItem.detail.longDescription,
          invoiceIndex,
          listPrice: lineItem.forcedListPrice, // Force prices are undefined if they don't exist
          netPrice: lineItem.forcedNetPrice,
          ns: false,
          oh,
          partNum: lineItem.partNum,
          req,
          stockClass: lineItem.detail.stockClass,
          sufficientQty,
          tradePrice: lineItem.forcedTradePrice,
        };

        // Non-Stocking Part
      } else if (lineItem.nonStockingPartNum && mode === 'NOW') {
        extractedParts[nextIndex++] = {
          desc: lineItem.nonStockingPartDescription!,
          invoiceIndex,
          listPrice: lineItem.nonStockingPartListPrice,
          netPrice: lineItem.nonStockingPartNetPrice,
          ns: true,
          oh: 0,
          partNum: lineItem.nonStockingPartNum,
          priceSymbol: lineItem.priceSymbol,
          req: lineItem.quantity,
          stockClass: lineItem.stockClass!,
          sufficientQty,
          tradePrice: lineItem.nonStockingPartTradePrice,
        };

        // Invoice comment/memo
      } else if (lineItem.comment) {
        /* The main invoice model assigns a partNum of "LINEITEM_COMMENT" to comments/memos.
           Within this module you will also see "PO_COMMENT" which refers to comments added with
           the PO Comment entry box on the modal itself.  The difference is the former is linked
           to a candidate position index while the latter is not.
        */
        extractedParts[nextIndex++] = {
          desc: lineItem.comment,
          invoiceIndex,
          ns: false,
          oh: 0,
          partNum: 'LINEITEM_COMMENT',
          req: 0,
          stockClass: '',
          sufficientQty: true,
        };
      }
      // else default part (not a need thus far), split supercedence edge case, etc.
    });

    return extractedParts;
  });

  // The PO builder does all state tracking with IDs only with eventual reference to the parts data structure.
  // This calculation is used in multiple places, so no reason to recalc repeatedly since the extracted parts won't change.
  const [partIndexes] = useState(Object.keys(parts));

  // The default PO template is only used by "NOW/Emergency POs"
  const [defaultPO] = useState(() => {
    // Seed default PO field controls from the model
    const newDefaultPO = _.clone(poTemplate);

    // Priority Order: 1. Ship to address override, 2. Ship to account override, 3. Loaded customer
    if (model.shipToAddress) {
      newDefaultPO.directShipOtherName = model.shipToAddress.name;
      newDefaultPO.directShipOtherAddress = model.shipToAddress.address;
      newDefaultPO.directShipOtherCityStateZip = `${model.shipToAddress.city}, ${model.shipToAddress.state} ${
        model.shipToAddress.zipCode.split('.')[0]
      }`;
      if (newDefaultPO.directShipOtherCityStateZip.startsWith(',')) {
        newDefaultPO.directShipOtherCityStateZip = newDefaultPO.directShipOtherCityStateZip.slice(1).trimLeft();
      }
      newDefaultPO.directShipOtherPhone = model.shipToAddress.phone;
    } else if (model.shipToAccount) {
      newDefaultPO.directShipCustomerNum = model.shipToAccount.customerID;
    } else {
      newDefaultPO.directShipCustomerNum = model.activeCustomerID!;
    }

    return newDefaultPO;
  });

  const [bo, setBO] = useState(() => {
    // Seed the confirmation detail controls from the model
    const newBO = _.cloneDeep(boTemplate);
    newBO.confirmationDetail.contactName = model.customerParams.name1;
    newBO.confirmationDetail.contactPhone = model.customerParams.phone;
    newBO.confirmationDetail.contactEmail = '';

    if (model.sysParams.seedConfirmationEmail) {
      if (model.customerParams.confirmationEmail) {
        newBO.confirmationDetail.contactEmail = model.customerParams.confirmationEmail;
      } else if (model.sysParams.seedConfirmationEmailInvoiceEmailFallback && model.customerParams.email) {
        newBO.confirmationDetail.contactEmail = model.customerParams.email;
      }
    }

    newBO.purchaseOrders[0].poDetail = _.clone(defaultPO);

    // Ship to overrides
    if (model.shipToAddress) {
      if (model.shipToAddress.name) {
        newBO.confirmationDetail.contactName = model.shipToAddress.name;
      }
      if (model.shipToAddress.phone) {
        newBO.confirmationDetail.contactPhone = model.shipToAddress.phone;
      }
      // COD accounts are not likely to have email addresses on file,
      // and the main UI does not ask for email on free-form ship to addresses
    } else if (model.shipToAccount) {
      if (model.shipToAccount.name1) {
        newBO.confirmationDetail.contactName = model.shipToAccount.name1;
      }
      if (model.shipToAccount.phone) {
        newBO.confirmationDetail.contactPhone = model.shipToAccount.phone;
      }
      if (model.shipToAccount.email) {
        newBO.confirmationDetail.contactEmail = model.shipToAccount.email;
      }
    }

    if (model.po) {
      newBO.confirmationDetail.customerPO = model.po;
    }

    if (
      model.runMode === 'PIN1' &&
      model.sysParams.defaultConfirmationShipOut &&
      model.activeRoutingMethod &&
      model.sysParams.shippingCodes.find(o => o.code === model.activeRoutingMethod)
    ) {
      newBO.confirmationDetail.shipOut = model.activeRoutingMethod;
    }

    return newBO;
  });

  // react-beautiful-dnd doesn't track drag state the precise way we require
  const [dragState, setDragState] = useState({ isDragging: false, id: '0' });

  // This hook tracks the selected state of candidate parts (multi-select)
  const [partSelectionToggles, setPartSelectionToggles] = useState(initPartSelectionToggleMap(partIndexes));
  const selectedPartCount = Object.values(partSelectionToggles).filter(x => x).length;

  const [activePO, setActivePO] = useState(0); // track active PO tab
  const [candidatePartList, setCandidatePartList] = useState(partIndexes);

  // default action inputs at bottom of modal - applies default action on drag
  let [defaultBOAction, setDefaultBOAction] = useState<POPartLineAction>(() => {
    let defaultBOAction = '';
    switch (model.sysParams.confirmationDefaultAction) {
      case 'holdOnHand':
        defaultBOAction = 'HOLD_ON_HAND';
        break;
      case 'orderFullQty':
        defaultBOAction = 'ORDER_FULL_QUANTITY';
        break;
      case 'invoiceOnHand':
      default:
        defaultBOAction = 'INVOICE_ON_HAND';
        break;
    }
    return defaultBOAction as POPartLineAction;
  });
  const [defaultPrebill, setDefaultPrebill] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);

  let [showAllParts, setShowAllParts] = useState(false);

  const [miscVendorNums, setMiscVendorNums] = useState([] as string[]);
  const [poCommentLineIsEditing, setPoCommentLineIsEditing] = useState(false);

  // Note: a missing suggestion for a particular stock class indicates the stock class is invalid
  //       an empty array for a particular stock class indicates no default vendors were set for that stock class
  const [vendorSuggestions, setVendorSuggestions] = useState<IVendorSuggestions>({ ATB13MISC: [] });

  // These are the caluclated vendor suggestions per PO (indexed on PO #) based on aggregate stock classes on the PO.
  const [derivedVendorSuggestions, setDerivedVendorSuggestions] = useState<IDerivedVendorSuggestion[]>([]);

  // We need arrays of refs which can dynmically grow - thus using "old school" refs rather than useRef hooks
  const [directShipCustomerLookupRefs] = useState<CustomerLookup[]>([]);
  const [directShipVendorLookupRefs] = useState<VendorLookup[]>([]);
  const [vendorSearchLookupRefs] = useState<VendorLookup[]>([]);

  // Static refs not tied to dynamic POs
  const customerPoRef = useRef<HTMLInputElement>(null);
  const customerPhoneRef = useRef<HTMLInputElement>(null);
  const customerFaxRef = useRef<HTMLInputElement>(null);
  const freightRef = useRef<HTMLInputElement>(null);
  const fuelRef = useRef<HTMLInputElement>(null);
  const poCommentInputRef = useRef<HTMLInputElement>(null); // Main comment input
  const poCommentEditInputRef = useRef<HTMLInputElement>(null); // Dynamic per PO edit-mode line
  const priorityCodeSelectRef = useRef<HTMLSelectElement>(null); // For on mount auto-focus (DAILY orders)
  const shipInSelectRef = useRef<HTMLSelectElement>(null); // For on mount auto-focus (NOW POs)

  /**** END STATE HOOKS ****/

  /**** MEMO HOOKS ****/

  // This memo hook prevents the need to re-render (and re-order) the derived suggested vendors on each render,
  // which could be costly since we're using controlled inputs and this would occur on every keystroke.
  const renderedDerivedVendorSuggestions = useMemo(() => {
    const maxIndex = bo.purchaseOrders.length;
    const result = [];

    for (let poIndex = 0; poIndex < maxIndex; poIndex++) {
      if (derivedVendorSuggestions[poIndex]) {
        result[poIndex] = Object.keys(derivedVendorSuggestions[poIndex])
          .sort(
            (a, b) =>
              derivedVendorSuggestions[poIndex][b].stockClasses.size -
              derivedVendorSuggestions[poIndex][a].stockClasses.size
          )
          .map((vendorNum, vKey) => {
            const vDetails = derivedVendorSuggestions[poIndex][vendorNum];
            return (
              <option
                value={vendorNum}
                key={vKey}
                title={`${vDetails.state} ${vDetails.city} ${vDetails.phone}`.trim()}
              >
                {`${vendorNum}: ${vDetails.name}`}
              </option>
            );
          });
      }
    }

    return result;
  }, [derivedVendorSuggestions]);

  // A fast transposition map to deal with the sequential render index demands of rb-dnd v12+
  // [renderIndex]: candidatePartListIndex
  const candidatePartListTranspositionMap = useMemo(() => {
    const map = [];
    let nextIndex = 0;
    for (let i = 0; i < candidatePartList.length; i++) {
      if (showAllParts) {
        map[nextIndex++] = i;
      } else {
        if (!(parts[candidatePartList[i]] as ICandidatePartDetail).sufficientQty) {
          map[nextIndex++] = i;
        }
      }
    }
    return map;
  }, [candidatePartList, showAllParts]);

  // Perf feature for calculating the visible candidate length (based on "Show All" flag)
  const visibleCandidatePartListLength: number = useMemo(
    () =>
      candidatePartList.reduce((count: number, partIndex: string) => {
        const sufficientQty = (parts[partIndex] as ICandidatePartDetail).sufficientQty;
        return !sufficientQty || (sufficientQty && showAllParts) ? count + 1 : count;
      }, 0),
    [candidatePartList, showAllParts]
  );

  // Perf feature - no need to calculate this every render
  const codesCache = useMemo(() => {
    const { backorderPriorityCodes, backorderWarehouseCodes, backorderShipViaCodes, shippingCodes } = model.sysParams;

    // HELD ORDER (ship out still set by clerk)
    const hoDoPc = _.find(backorderPriorityCodes, o => o.desc.toUpperCase() === 'HELD ORDER')?.code ?? '';
    const hoDoSc = _.find(backorderWarehouseCodes, o => o.desc.toUpperCase() === 'HELD ORDER')?.code ?? '';
    const hoSi = _.find(backorderShipViaCodes, o => o.desc.toUpperCase() === 'HELD ORDER')?.code ?? '';
    const hoEnabled = !!hoDoPc && !!hoDoSc && !!hoSi;

    // CONSIGNMENT
    const coDoPc = _.find(backorderPriorityCodes, o => o.desc.toUpperCase() === 'CONSIGNMENT')?.code ?? '';
    const coDoSc = _.find(backorderWarehouseCodes, o => o.desc.toUpperCase() === 'CONSIGNMENT')?.code ?? '';
    const coSi = _.find(backorderShipViaCodes, o => o.desc.toUpperCase() === 'CONSIGNMENT')?.code ?? '';
    const coSo = _.find(shippingCodes, o => o.desc.toUpperCase() === 'CONSIGNMENT')?.code ?? '';
    const coEnabled = !!coDoPc && !!coDoSc && !!coSi && !!coSo;

    return { hoEnabled, hoDoPc, hoDoSc, hoSi, coEnabled, coDoPc, coDoSc, coSi, coSo };
  }, []);

  // Shortcuts are not cached since they need updated function references in the executors
  const shortcuts = {
    HELDORDER: {
      enabled: codesCache.hoEnabled,
      execute: () => {
        updateConfirmationField('dailyOrderPriorityCode', codesCache.hoDoPc);
        updateConfirmationField('dailyOrderShippingCode', codesCache.hoDoSc);
        updateConfirmationField('shipIn', codesCache.hoSi);
        updateConfirmationField('shipOut', blank);
        handleShowAllParts(true);
        setDefaultBOAction('HOLD_ON_HAND'); // next cycle
        defaultBOAction = 'HOLD_ON_HAND'; // this cycle
        addAllPartsToPO();
      },
    },
    CONSIGNMENT: {
      enabled: codesCache.coEnabled,
      execute: () => {
        updateConfirmationField('dailyOrderPriorityCode', codesCache.coDoPc);
        updateConfirmationField('dailyOrderShippingCode', codesCache.coDoSc);
        updateConfirmationField('shipIn', codesCache.coSi);
        updateConfirmationField('shipOut', codesCache.coSo);
        handleShowAllParts(true);
        setDefaultBOAction('HOLD_ON_HAND'); // next cycle
        defaultBOAction = 'HOLD_ON_HAND'; // this cycle
        addAllPartsToPO();
      },
    },
  };

  /**** END MEMO HOOKS ****/

  /**** EFFECT HOOKS ****/

  // Focus to priority code / ship-in (daily/now) field on mount so clerk can immediately type a code and tab
  useEffect(() => {
    if (mode === 'DAILY') {
      if (priorityCodeSelectRef && priorityCodeSelectRef.current) {
        priorityCodeSelectRef.current.focus();
      }
    }

    if (mode === 'NOW') {
      if (shipInSelectRef && shipInSelectRef.current) {
        shipInSelectRef.current.focus();
      }
    }
  }, []);

  // Fetch all possible vendor suggestions in 1 shot on mount
  useEffect(() => {
    const fetchVendorSuggestions = async () => {
      const result = await model.getVendorSuggestions(parts.filter(x => x.stockClass !== '').map(y => y.stockClass));
      setVendorSuggestions(result);

      const mvNums = [] as string[];
      result.ATB13MISC.forEach(mv => mvNums.push(mv.num));
      setMiscVendorNums(mvNums);

      if (model.logging) {
        console.warn('Vendor Suggestions / Misc Vendor Nums');
        console.log(result);
        console.log(mvNums);
      }
    };
    fetchVendorSuggestions();
  }, []);

  // Hydrate-Rehydrate the react-autosuggest controls when necessary.
  // Wait briefly for RBS to possibly render / remove new tabs.
  useEffect(() => {
    if (mode === 'NOW') {
      setTimeout(() => {
        bo.purchaseOrders.forEach((purchaseOrder, index) => {
          directShipCustomerLookupRefs[index].value = purchaseOrder.poDetail.directShipCustomerNum;
          directShipVendorLookupRefs[index].value = purchaseOrder.poDetail.directShipVendorNum;
          vendorSearchLookupRefs[index].value = purchaseOrder.poDetail.vendorSearch;
        });
      }, 150);
    }
  }, [bo]);

  // Only run this hook when parts are added to / removed from a purchase order
  // Note: this requires thoughtful ref handling when cloning the bo object.
  useEffect(() => {
    const newDerivedVendorSuggestions: IDerivedVendorSuggestion[] = [];

    bo.purchaseOrders.forEach((po, poIndex) => {
      const activeStockClassesThisPO = new Set<string>();
      const vendorStockClassMatches = {} as IDerivedVendorSuggestion;

      // Derive unique set of active stock classes on this PO
      po.parts.forEach(part => {
        if ('poComment' in part) return;

        const partDetails = parts[part.partIndex] as ICandidatePartDetail;
        if (partDetails.stockClass !== '') {
          activeStockClassesThisPO.add(partDetails.stockClass);
        }
      });

      activeStockClassesThisPO.forEach(stockClass => {
        if (vendorSuggestions[stockClass]) {
          vendorSuggestions[stockClass].forEach(vendorDetails => {
            if (vendorStockClassMatches[vendorDetails.num]) {
              vendorStockClassMatches[vendorDetails.num].stockClasses.add(stockClass);
            } else {
              vendorStockClassMatches[vendorDetails.num] = {
                city: vendorDetails.city,
                name: vendorDetails.name,
                phone: vendorDetails.phone,
                state: vendorDetails.state,
                stockClasses: new Set([stockClass]),
              };
            }
          });
        }
      });

      newDerivedVendorSuggestions[poIndex] = vendorStockClassMatches;
    });

    setDerivedVendorSuggestions(newDerivedVendorSuggestions);

    /* There's a possibility a user just deleted a part and its associated selected vendor suggestion.
       If so, clear the previously suggested value from the backorder state model as well.  This would
       only occur on the "active" PO.
    */
    const activeSuggestedVendor = bo.purchaseOrders[activePO].poDetail.suggestedVendor;
    if (
      activeSuggestedVendor !== blank &&
      !miscVendorNums.includes(activeSuggestedVendor) &&
      !(activeSuggestedVendor in newDerivedVendorSuggestions[activePO])
    ) {
      const newBO = _.clone(bo);
      newBO.purchaseOrders[activePO].poDetail.suggestedVendor = blank;
      setBO(newBO);
    }
  }, [bo.purchaseOrders]);

  // This hook auto-selects the PO comment line text so it's fast to replace
  useEffect(() => {
    if (poCommentLineIsEditing) {
      const el = poCommentEditInputRef.current!;
      el.focus();
      el.setSelectionRange(0, el.value.length);
    }
  }, [poCommentLineIsEditing]);

  /**** END EFFECT HOOKS ****/

  /**** EVENT HANDLING & UTILITY METHODS ****/

  // User wants to shortcut putting all candidate parts on active PO (very common)
  const addAllPartsToPO = async () => {
    if (isProcessing) {
      return;
    }

    const newBO = _.cloneDeep(bo); // need fresh PO ref for suggested vendors hook
    const activePOParts = newBO.purchaseOrders[activePO].parts;
    const hiddenParts: string[] = []; // used to keep "sufficientQty" parts on the candidate list (hidden)

    let includeComments = false;

    // Figure out if we should include comments in "Show All Parts" mode before moving anything (if they exist)
    if (
      showAllParts &&
      candidatePartList.some(partIndex => (parts[partIndex] as ICandidatePartDetail).partNum === 'LINEITEM_COMMENT')
    ) {
      includeComments = await new Promise(resolve => {
        modal.confirm({
          title: 'Comments',
          message: 'Include comments / memos?',
          confirmLabels: ['Yes', 'No'],
          callback: () => resolve(true),
          onClose: () => resolve(false),
        });
      });
    }

    candidatePartList.forEach(partIndex => {
      const partDetails = parts[partIndex] as ICandidatePartDetail;

      if (partDetails.sufficientQty && !showAllParts) {
        // keep all parts of sufficient qty and comments in the "hidden" list
        // in case "show all parts" is eventually checked
        hiddenParts.push(partIndex);

        // otherwise move all parts (including "hidden" ones), unless comments are suppressed
      } else {
        if (!includeComments && partDetails.partNum === 'LINEITEM_COMMENT') {
          hiddenParts.push(partIndex);
        } else {
          activePOParts.push(createPOPart(partIndex));
        }
      }
    });

    setBO(newBO);
    setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
    setCandidatePartList(hiddenParts);
  };

  // User wishes to attach an additional PO to the confirmation
  const addPO = () => {
    if (isProcessing) {
      return;
    }

    const newBO = _.clone(bo);
    newBO.purchaseOrders.push({
      parts: [],
      poDetail: _.clone(defaultPO),
    });
    setBO(newBO);
  };

  // Used by the PO comment entry box, not by comments tracked from the model
  const addPOComment = () => {
    if (isProcessing) {
      return;
    }

    const newBO = _.clone(bo);
    newBO.purchaseOrders[activePO].parts.push({
      boAction: 'ORDER_FULL_QUANTITY', // IGNORED
      partIndex: `PO_COMMENT_${uuidv4()}`,
      poComment: poCommentInputRef.current!.value.trim(),
      poCommentIsEditing: false,
      prebill: false, // IGNORED
    });

    poCommentInputRef.current!.value = '';
    setBO(newBO);
  };

  // User is changing the backorder action or prebill status of a part line
  const changePartLineActions = ({
    action,
    partLineIndex,
    poIndex,
    prebill,
  }: {
    action?: POPartLineAction | undefined;
    partLineIndex: number;
    poIndex: number;
    prebill?: boolean | undefined;
  }) => {
    if (isProcessing) {
      return;
    }

    const newBO = _.clone(bo);
    if (action) {
      newBO.purchaseOrders[poIndex].parts[partLineIndex].boAction = action;
      if (action === 'HOLD_ON_HAND') {
        newBO.purchaseOrders[poIndex].parts[partLineIndex].prebill = false;
      }
    }
    if (!_.isUndefined(prebill)) {
      newBO.purchaseOrders[poIndex].parts[partLineIndex].prebill = prebill;
    }
    setBO(newBO);
  };

  // User has selected multiple parts for a multipart drag, but wants to clear selection
  const clearPartSelection = () => {
    setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
  };

  // Given a partIndex, return a data structure suitable for PO placement
  const createPOPart = (partIndex: string): IPOPart => {
    const partDetails = parts[partIndex] as ICandidatePartDetail;
    return partDetails.partNum === 'LINEITEM_COMMENT'
      ? {
          boAction: 'ORDER_FULL_QUANTITY', // IGNORED
          partIndex,
          poComment: partDetails.desc,
          poCommentIsEditing: false,
          prebill: false, // IGNORED
        }
      : {
          boAction: getInitialPOPartAction(partIndex),
          partIndex,
          prebill: getInitialPOPartPrebill(partIndex),
        };
  };

  // Return all parts on the active PO to the candidate part list, then delete the PO
  // The manual CSS toggling to the panes simulates the manual tabbing animation the user expects.
  const deletePO = () => {
    if (isProcessing) {
      return;
    }

    const newPartList = _.clone(candidatePartList);
    const activePOParts = bo.purchaseOrders[activePO].parts;
    activePOParts.forEach(part => {
      // Skip comment lines
      if (!('poComment' in part)) {
        newPartList.push(part.partIndex);
      }
    });
    setCandidatePartList(newPartList);

    const newBO = _.cloneDeep(bo); // need fresh PO refs for suggested vendors hook
    if (newBO.purchaseOrders.length === 1) {
      newBO.purchaseOrders[0] = {
        parts: [],
        poDetail: _.clone(defaultPO),
      };
    } else {
      newBO.purchaseOrders.splice(activePO, 1);
    }
    setBO(newBO);

    const flashPO = (po: number) => {
      document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.remove('active', 'show');
      setTimeout(() => {
        document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.add('active');
        setTimeout(() => {
          document.getElementById(`sk-poNavigator-tabpane-po-${po}`)!.classList.add('show');
        }, 120);
      }, 120);
    };

    // Last PO in attachment list deleted?
    if (activePO === newBO.purchaseOrders.length) {
      setActivePO(activePO - 1);
      setTimeout(() => {
        flashPO(activePO - 1);
      }, 25);
    } else {
      flashPO(activePO);
    }
  };

  // When dropping over a PO, we want the effect of the part dropping in the PO part list area
  // This only effects the first part dropped.
  const getDraggableStyle = (style: any, snapshot: any, poEmpty: boolean) => {
    if (snapshot.isDropAnimating && snapshot.draggingOver && snapshot.draggingOver.startsWith('po-') && poEmpty) {
      const { moveTo } = snapshot.dropAnimation;
      // move to the right spot
      const translate = `translate(${moveTo.x + 15}px, ${moveTo.y + (mode === 'DAILY' ? 15 : 200)}px)`;
      return {
        ...style,
        transform: `${translate}`,
      };
    } else {
      return style;
    }
  };

  // The next two functions need to be re-called on each drag since the default backorder actions could change.

  const getInitialPOPartAction = (partIndex: string): POPartLineAction => {
    const partDetails = parts[partIndex] as ICandidatePartDetail;
    if (partDetails.sufficientQty) {
      return defaultBOAction === 'INVOICE_ON_HAND'
        ? model.sysParams.noHeldOrders
          ? 'ORDER_FULL_QUANTITY'
          : 'HOLD_ON_HAND'
        : defaultBOAction;
    } else if (partDetails.oh <= 0) {
      return 'ORDER_FULL_QUANTITY';
    } else {
      return defaultBOAction;
    }
  };

  const getInitialPOPartPrebill = (partIndex: string): boolean => {
    const partDetails = parts[partIndex] as ICandidatePartDetail;
    if (
      (partDetails.sufficientQty && defaultBOAction === 'HOLD_ON_HAND') ||
      (partDetails.sufficientQty && defaultBOAction === 'INVOICE_ON_HAND' && !model.sysParams.noHeldOrders) ||
      (defaultBOAction === 'HOLD_ON_HAND' && partDetails.oh > 0)
    ) {
      return false;
    } else {
      return defaultPrebill;
    }
  };

  const handleShowAllParts = (show: boolean) => {
    showAllParts = show; // this cycle (scripted shorcuts)
    setShowAllParts(show); // next cycle
    clearPartSelection();
  };

  // If vendor search or suggested vendor is set to a misc vendor, return true.  The former dominates.
  const isMiscVendorSelected = () => {
    const activeSuggestedVendor = bo.purchaseOrders[activePO].poDetail.suggestedVendor;
    const activeVendorSearch = extractVendorNum(bo.purchaseOrders[activePO].poDetail.vendorSearch.trim());
    const activeVendor = activeVendorSearch || activeSuggestedVendor;

    return miscVendorNums.includes(activeVendor);
  };

  const onDragEnd = (result: DropResult) => {
    setDragState({ isDragging: false, id: '0' });

    // Not on a droppable
    if (!result.destination || isProcessing) return;

    /* react-beautiful-dnd requires sequential indexes for proper operation, and thus only works upon
       "shown" indexes.  This creates a problem if "show parts" isn't checked, because now the indexes
       within the candidate part list are not 1:1 with the "indexes" of the draggables in the list.
       Thus, we have to transpose when doing our splices when showAllParts is off (the transposed values
       are also correct when showAllParts is on, it's a simple 1:1 mapping).  We only use transposition
       mapping when referring to candidate parts (i.e. po line movement can rely direclty on rdnd indexes).
    */

    // Move item within candidate part list
    if (
      result.source.droppableId === 'partList' &&
      result.destination.droppableId === 'partList' &&
      result.source.index !== result.destination.index
    ) {
      if (selectedPartCount > 1) {
        // TODO: support for multi part drag within the candidate list - not that important
      } else {
        const newPartList = _.clone(candidatePartList);
        newPartList.splice(
          candidatePartListTranspositionMap[result.destination.index],
          0,
          newPartList.splice(candidatePartListTranspositionMap[result.source.index], 1)[0]
        );
        setCandidatePartList(newPartList);
      }
      return;
    }

    // Place item on an attached PO
    if (result.source.droppableId === 'partList' && result.destination.droppableId.startsWith('po-')) {
      // Add part to the PO tab the user dropped on
      const newBO = _.cloneDeep(bo); // need fresh PO ref for suggested vendors hook
      const poID = parseInt(result.destination.droppableId.slice(3), 10) || 0;
      const insertIndex = result.destination.index;
      let newPartList;

      // Multi-part Drag
      if (selectedPartCount > 1) {
        const poParts: IPOPart[] = [];
        for (const partIndex of Object.keys(partSelectionToggles)) {
          if (partSelectionToggles[partIndex]) {
            poParts.push(createPOPart(partIndex));
          }
        }
        newBO.purchaseOrders[poID].parts.splice(insertIndex, 0, ...poParts);
        newPartList = candidatePartList.filter(id => !partSelectionToggles[id]);

        // Single Part Drag
      } else {
        newBO.purchaseOrders[poID].parts.splice(insertIndex, 0, createPOPart(result.draggableId));
        newPartList = candidatePartList.filter(id => id !== result.draggableId);
      }

      setBO(newBO);
      setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
      setCandidatePartList(newPartList);

      return;
    }

    // Move line within PO
    if (
      result.source.droppableId.startsWith('po-') &&
      result.destination.droppableId.startsWith('po-') &&
      result.source.index !== result.destination.index
    ) {
      const newBO = _.clone(bo);
      const lines = newBO.purchaseOrders[activePO].parts; // PO Part lines

      lines.splice(result.destination.index, 0, lines.splice(result.source.index, 1)[0]);

      setBO(newBO);
      return;
    }
  };

  const onBeforeDragStart = (start: DragStart) => {
    setDragState({ isDragging: true, id: start.draggableId });

    // If the user ends up dragging a part which wasn't part of a previously set
    // multi-selection group, clear out the selection group.
    if (selectedPartCount > 1 && !partSelectionToggles[start.draggableId]) {
      setPartSelectionToggles(initPartSelectionToggleMap(partIndexes));
    }
  };

  // This is used for multi part selection / deselection.
  // It is not called when a user does a long click + drag.
  const onPartClick = (event: React.MouseEvent, id: string) => {
    // Prob will never use this, but it's stubbed out in case people ask for it.
    if (event.shiftKey) {
      // Select all parts between first & last items clicked
    }
    if (event.ctrlKey) {
      // Multi select / does not clear other items selected
    }

    const newSelectedParts = _.clone(partSelectionToggles);
    newSelectedParts[id] = !newSelectedParts[id];
    setPartSelectionToggles(newSelectedParts);
  };

  // Track the active tab for actions such as adding all candidate parts, delete PO, etc.
  // Per RBS4 type declarations, eventKey is string | null (not sure what condition would cause null)
  const onPOTabSelect = (eventKey: string | null) => {
    if (eventKey) {
      setActivePO(parseInt(eventKey.slice(3), 10));
    }
  };

  // Process the Confirmation
  const process = async () => {
    // Here we link/merge the candidate part details into the finalized backorder submission data structure
    const boSubmission: IBackorderSubmission = {
      isRO,
      confirmationDetail: _.clone(bo.confirmationDetail),
      customerID: model.activeCustomerID!, // vestigial in PRO1/SMM3 cases
      mode,
      ppc: model.ppcParams.ppc,
      purchaseOrders: [], // "Finalized" purchase orders, linked to candidate part details
      roNum: model.activeRONumber || '', // only used by PRO1/SMM3
    };

    bo.purchaseOrders.forEach(po => {
      // Note some of the "finalized" properties are extracted from the PO part line data structure,
      // while others come from the candidate backorder parts data structure.
      const finalizedPOParts: FinalizedPOPart[] = [];
      po.parts.forEach(part => {
        let finalizedPOPart: FinalizedPOPart;

        if ('poComment' in part) {
          finalizedPOPart = {
            poComment: part.poComment!,
          };
        } else {
          const partDetails = parts[part.partIndex] as ICandidatePartDetail;
          finalizedPOPart = {
            boAction: part.boAction,
            desc: partDetails.desc,
            invoiceIndex: partDetails.invoiceIndex,
            listPrice: partDetails.listPrice,
            netPrice: partDetails.netPrice,
            ns: partDetails.ns,
            oh: partDetails.oh,
            partNum: partDetails.partNum,
            prebill: part.prebill,
            priceSymbol: partDetails.priceSymbol,
            req: partDetails.req,
            stockClass: partDetails.stockClass,
            tradePrice: partDetails.tradePrice,
          };

          if (partDetails.supercedenceEscalationFactor !== 1) {
            finalizedPOPart.supercedenceEscalationFactor = partDetails.supercedenceEscalationFactor;
          }
        }

        finalizedPOParts.push(finalizedPOPart);
      });

      boSubmission.purchaseOrders.push({
        parts: finalizedPOParts,
        poDetail: { ..._.clone(po.poDetail), vendorSearch: extractVendorNum(po.poDetail.vendorSearch) },
      });
    });

    if (model.logging) {
      console.log('Backorder State / Submission Request:');
      console.log({
        boBuilder: bo,
        boSubmission,
        candidateParts: parts,
      });
    }

    const success = await model.processBackorder(boSubmission, hide);

    if (success) {
      /* On success, processBackorder() will hide this modal for us, then pop the printer selector.
         The model will also update the invoice screen (applies ex-post actions) automatically in
         the background while the user handles their printing needs.
      */
    } else {
      setIsProcessing(false);
    }
  };

  // User has clicked the trash icon - remove part from PO and return to candidate part list
  const removePartLine = ({ partLineIndex, poIndex }: IPOLocation) => {
    if (isProcessing) {
      return;
    }

    // Skip "PO Comment" comment lines added from modal directly
    if (!bo.purchaseOrders[poIndex].parts[partLineIndex].partIndex.startsWith('PO_COMMENT_')) {
      const newPartList = _.clone(candidatePartList);
      newPartList.push(bo.purchaseOrders[poIndex].parts[partLineIndex].partIndex);
      setCandidatePartList(newPartList);
    }

    const newBO = _.cloneDeep(bo); // need fresh PO ref for suggested vendors hook
    newBO.purchaseOrders[poIndex].parts.splice(partLineIndex, 1);
    setBO(newBO);
  };

  // When set, the PO comment line becomes editable
  const setCommentIsEditing = ({ partLineIndex, poIndex }: IPOLocation) => {
    const newBO = _.clone(bo);
    newBO.purchaseOrders[poIndex].parts[partLineIndex].poCommentIsEditing = true;
    setBO(newBO);
    setPoCommentLineIsEditing(true);
  };

  // Called by the onChange handler of every confirmation input field - this should be very fast in the VDOM
  const updateConfirmationField = <T extends keyof IConfirmationDetail>(field: T, value: IConfirmationDetail[T]) => {
    if (isProcessing) {
      return;
    }

    const newBO = _.clone(bo);
    newBO.confirmationDetail[field] = value;
    setBO(newBO);
  };

  const updatePartLineComment = ({ partLineIndex, poIndex }: IPOLocation) => {
    const newBO = _.clone(bo);
    newBO.purchaseOrders[poIndex].parts[partLineIndex].poComment = poCommentEditInputRef.current!.value;
    newBO.purchaseOrders[poIndex].parts[partLineIndex].poCommentIsEditing = false;
    setBO(newBO);
    setPoCommentLineIsEditing(false);
  };

  // Called by the onChange handler of every po input field - this should be very fast in the VDOM
  const updatePOField = <T extends keyof IPODetail>(poIndex: number, field: T, value: IPODetail[T]) => {
    if (isProcessing) {
      return;
    }

    const newBO = _.clone(bo);
    newBO.purchaseOrders[poIndex].poDetail[field] = value;
    setBO(newBO);
  };

  /* This is a basic field validator which makes sure all confirmations and POs are properly filled out
     We'll eventually enhance this with per field error highlighting, but this will do for now (or maybe forever).
     Short circuits on first failed test.
  */
  const validateConfirmation = () => {
    const fail = (reason: string, toastId?: string) => {
      if (reason !== '') {
        toastr.error('Error', reason, { toastId });
      }
      return false;
    };

    if (mode === 'DAILY') {
      if (bo.confirmationDetail.dailyOrderPriorityCode === blank) {
        return fail('Daily Order Priority Code Not Set');
      }
      if (bo.confirmationDetail.dailyOrderShippingCode === blank) {
        return fail('Daily Order Shipping Code Not Set');
      }
      if (bo.purchaseOrders[0].parts.length === 0) {
        return fail('No Parts to Process');
      }
    }

    if (bo.confirmationDetail.shipIn === blank) {
      return fail('Confirmation Ship In Not Set');
    }

    if (!isRO) {
      if (bo.confirmationDetail.shipOut === blank) {
        return fail('Confirmation Ship Out Not Set');
      }

      const customerPO = bo.confirmationDetail.customerPO.trim();
      if (model.customerParams.purchaseOrderRequired) {
        if (customerPO === '') {
          return fail('Confirmation Customer PO# Required');
        }
        if (!model.validatePo(customerPO)) {
          return fail('Invalid Customer PO format.', 'customerPo');
        }
      }

      // We show the following fields in RO cases, but PIN1 has different requirements here
      if (bo.confirmationDetail.contactName.trim() === '') {
        return fail('Confirmation Contact Name Not Set');
      }
      if (bo.confirmationDetail.contactPhone.trim() === '') {
        return fail('Confirmation Contact Phone Not Set');
      }
      if (!validatePhoneField(bo.confirmationDetail.contactPhone)) {
        return fail('Contact phone requires 10 digits.', 'contactPhone');
      }
      if (bo.confirmationDetail.contactFax !== '' && !validatePhoneField(bo.confirmationDetail.contactFax)) {
        return fail('Contact fax requires 10 digits.', 'contactFax');
      }
    }

    // Validate phone fields on RO backorders if utilized.  Not required in RO cases.
    if (isRO) {
      if (bo.confirmationDetail.contactPhone !== '' && !validatePhoneField(bo.confirmationDetail.contactPhone)) {
        return fail('Contact fax requires 10 digits.', 'contactPhone');
      }
      if (bo.confirmationDetail.contactFax !== '' && !validatePhoneField(bo.confirmationDetail.contactFax)) {
        return fail('Contact fax requires 10 digits.', 'contactFax');
      }
    }

    if (mode === 'NOW') {
      // Validate the fields of each "attached" PO
      const poMaxLength = bo.purchaseOrders.length;
      for (let poIndex = 0; poIndex < poMaxLength; poIndex++) {
        const po = bo.purchaseOrders[poIndex];
        let vendorSearch = po.poDetail.vendorSearch.trim();
        let directShipCustomerNum = po.poDetail.directShipCustomerNum.trim();
        let directShipVendorNum = po.poDetail.directShipVendorNum.trim();

        /* React-autosuggest inputs could potentially be out of sync with po state during this render
           validation, so double check the DOM controls directly for updates to the state before validating.
           This occurs because these controls only update the state on selection or blur, not keystroke.
           Blur creates a state change on the _next_ render cycle (via hook), and a direct press of process
           by the user would call this function before the hook updates the state.  Do the DOM check as safely
           as possible.
        */
        let el;
        el = document.getElementById(`vendorSearchLookup${poIndex}`) as HTMLInputElement;
        if (el) {
          const vendorSearchDOM = el.value.trim();
          if (vendorSearchDOM !== vendorSearch) {
            vendorSearch = vendorSearchDOM;
            po.poDetail.vendorSearch = vendorSearch;
          }
        }
        el = document.getElementById(`directShipCustomerLookup${poIndex}`) as HTMLInputElement;
        if (el) {
          const directShipCustomerNumDOM = el.value.trim();
          if (directShipCustomerNumDOM !== directShipCustomerNum) {
            directShipCustomerNum = directShipCustomerNumDOM;
            po.poDetail.directShipCustomerNum = directShipCustomerNum;
          }
        }
        el = document.getElementById(`directShipVendorLookup${poIndex}`) as HTMLInputElement;
        if (el) {
          const directShipVendorNumDOM = el.value.trim();
          if (directShipVendorNumDOM !== directShipVendorNum) {
            directShipVendorNum = directShipVendorNumDOM;
            po.poDetail.directShipVendorNum = directShipVendorNum;
          }
        }

        let miscVendorSelected = false;

        if (po.parts.length === 0) {
          return fail(`PO ${poIndex + 1}: No Parts to Process`);
        }

        /* It's possible a PO is being created only to hold parts on hand, in which
           case no vendor assignment should be required since we're going straight HODC.
           Additionally, this may be a memo-only PO place holder.

           As of 2/17/20 this is a client side feature only.  QM still requires a vendor
           and will unfortunately return an error indicating such. Phil stated it would
           be too complicated to remove the requirement on his side. We have not thus far
           had any customer complaints.

           Noticed 1/28/21 if a vendor is not specified on the 2+ attached PO, QM writes out
           only a partial confirmation and returns both the partial confirmation # as well
           as an error.  We'll force client side vendor selection since it's inevitable anyways
           until Phil can support a full hold PO with no vendor specified.
        */
        // const vendorRequired = po.parts.some(part => {
        //   // Comments added via the backorder modal will not be in the model-derived parts[] lookup.
        //   if ('poComment' in part) return false;

        //   const partDetails = parts[part.partIndex] as ICandidatePartDetail;
        //   return !(partDetails.oh >= partDetails.req && part.boAction === 'HOLD_ON_HAND');
        // });
        const vendorRequired = true;

        if (vendorRequired && po.poDetail.suggestedVendor === blank && vendorSearch === '') {
          return fail(`PO ${poIndex + 1}: No Vendor Selected`);
        }

        // Vendor search dominates suggested vendor
        if (vendorSearch !== '') {
          if (miscVendorNums.includes(extractVendorNum(vendorSearch))) {
            miscVendorSelected = true;
          }
        } else if (miscVendorNums.includes(po.poDetail.suggestedVendor)) {
          miscVendorSelected = true;
        }

        if (miscVendorSelected) {
          if (po.poDetail.miscVendorName.trim() === '') {
            return fail(`PO ${poIndex + 1}: Misc Vendor Name Not Set`);
          }
          if (po.poDetail.miscAddress.trim() === '') {
            return fail(`PO ${poIndex + 1}: Misc Vendor Address Not Set`);
          }
          if (po.poDetail.miscCityStateZip.trim() === '') {
            return fail(`PO ${poIndex + 1}: Misc Vendor City/State/Zip Not Set`);
          }
          if (po.poDetail.miscPhone.trim() === '') {
            return fail(`PO ${poIndex + 1}: Misc Vendor Phone Not Set`);
          } else if (!validatePhoneField(po.poDetail.miscPhone)) {
            return fail(`PO ${poIndex + 1}: Misc vendor phone requires 10 digits.`, `PO${poIndex}MiscVendorPhone`);
          }
        }

        if (po.poDetail.directShipType === 'C' && directShipCustomerNum === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Customer # Not Set`);
        }
        if (po.poDetail.directShipType === 'V' && directShipVendorNum === '') {
          return fail(`PO ${poIndex + 1}: Direct Ship Vendor # Not Set`);
        }

        if (po.poDetail.directShipType === 'O') {
          if (po.poDetail.directShipOtherName.trim() === '') {
            return fail(`PO ${poIndex + 1}: Direct Ship Other Name Not Set`);
          }
          if (po.poDetail.directShipOtherAttention.trim() === '') {
            return fail(`PO ${poIndex + 1}: Direct Ship Other Attention Not Set`);
          }
          if (po.poDetail.directShipOtherAddress.trim() === '') {
            return fail(`PO ${poIndex + 1}: Direct Ship Other Address Not Set`);
          }
          if (po.poDetail.directShipOtherCityStateZip.trim() === '') {
            return fail(`PO ${poIndex + 1}: Direct Ship Other City/State/Zip Not Set`);
          }
          if (po.poDetail.directShipOtherPhone.trim() === '') {
            return fail(`PO ${poIndex + 1}: Direct Ship Other Phone Not Set`);
          } else if (!validatePhoneField(po.poDetail.directShipOtherPhone)) {
            return fail(
              `PO ${poIndex + 1}: Direct ship to "other" phone requires 10 digits.`,
              `PO${poIndex}DirectShipPhone`
            );
          }
        }
      }
    }

    return true;
  };

  // A valid phone number is any string which contains 10 digits, so (702) 530-2199 or 7025302199, for example.
  const validatePhoneField = (value: string) => {
    const containsTenDigits = /^\D*(\d\D*){10}$/;
    return containsTenDigits.test(value);
  };

  /**** END EVENT HANDLING & UTILITY METHODS ****/

  // Shared between Front/Back Counter backorders
  const renderConfirmationCharges = () => {
    return (
      <>
        <div className="sk-confirmationInputCharge">
          <Label>Freight</Label>
          <input
            type="text"
            defaultValue=""
            onBlur={() => {
              const ref = freightRef.current!;
              const freight = ref.value.trim();

              if (freight !== '') {
                if (isNaN(parseFloat(freight))) {
                  updateConfirmationField('freight', '');
                  toastr.error('Error', 'Freight must be numeric.', { toastId: 'freight' });
                } else {
                  // Format the value
                  const formattedFreight = parseFloat(freight).toFixed(2);
                  freightRef.current!.value = formattedFreight;
                  updateConfirmationField('freight', formattedFreight);
                }
              } else {
                updateConfirmationField('freight', '');
              }
            }}
            ref={freightRef}
          />
        </div>
        {['Y', 'F'].includes(model.sysParams.promptForFuel) && (
          <div className="sk-confirmationInputCharge">
            <Label>Fuel</Label>
            <input
              type="text"
              defaultValue=""
              onBlur={() => {
                const ref = fuelRef.current!;
                const fuel = ref.value.trim();

                if (fuel !== '') {
                  if (isNaN(parseFloat(fuel))) {
                    updateConfirmationField('fuel', '');
                    toastr.error('Error', 'Fuel must be numeric.', { toastId: 'fuel' });
                  } else {
                    // Format the value
                    const formattedFuel = parseFloat(fuel).toFixed(2);
                    fuelRef.current!.value = formattedFuel;
                    updateConfirmationField('fuel', formattedFuel);
                  }
                } else {
                  updateConfirmationField('fuel', '');
                }
              }}
              ref={fuelRef}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      size="lg"
      backdrop="static"
      container={container as any}
      dialogClassName="sk-backorderModal"
      onHide={() => null}
      show={show}
    >
      <Modal.Body>
        <Row>
          <Col xs={12}>
            <fieldset className="sk-confirmation" style={{ height: mode === 'DAILY' ? '240px' : '205px' }}>
              <legend>Confirmation Details</legend>
              <div className="sk-confirmationDetails">
                {mode === 'DAILY' && (
                  <>
                    <div className="sk-dailyParametersContainer">
                      <div>
                        <div className="sk-dailyOrderParametersLabel">Daily Order Classification:</div>
                        <div className="sk-dailyOrderParameters">
                          <select
                            onChange={evt => updateConfirmationField('dailyOrderPriorityCode', evt.target.value)}
                            value={bo.confirmationDetail.dailyOrderPriorityCode}
                            ref={priorityCodeSelectRef}
                          >
                            <option disabled={true} value={blank}>
                              Priority Code
                            </option>
                            {model.sysParams.backorderPriorityCodes.map(x => (
                              <option key={x.code} value={x.code}>
                                {x.code} - {x.desc}
                              </option>
                            ))}
                          </select>
                          <select
                            onChange={evt => updateConfirmationField('dailyOrderShippingCode', evt.target.value)}
                            value={bo.confirmationDetail.dailyOrderShippingCode}
                          >
                            <option disabled={true} value={blank}>
                              Shipping Code
                            </option>
                            {model.sysParams.backorderWarehouseCodes.map(x => (
                              <option key={x.code} value={x.code}>
                                {x.code} - {x.desc}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {!isRO && (shortcuts.HELDORDER.enabled || shortcuts.CONSIGNMENT.enabled) && (
                        <div className="sk-backorderShortcutModesContainer">
                          <div className="sk-dailyOrderParametersLabel">Shortcuts:</div>
                          {shortcuts.HELDORDER.enabled && (
                            <Button
                              size="sm"
                              variant="secondary"
                              tabIndex={-1}
                              onClick={() => shortcuts.HELDORDER.execute()}
                            >
                              Held Order
                            </Button>
                          )}
                          {shortcuts.CONSIGNMENT.enabled && (
                            <Button
                              size="sm"
                              variant="secondary"
                              tabIndex={-1}
                              onClick={() => shortcuts.CONSIGNMENT.execute()}
                            >
                              Consignment
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <hr />
                  </>
                )}
                <div className="sk-confirmationInput">
                  <Label>Ship In</Label>
                  <select
                    onChange={evt => updateConfirmationField('shipIn', evt.target.value)}
                    value={bo.confirmationDetail.shipIn}
                    ref={shipInSelectRef}
                  >
                    <option disabled={true} value={blank}>
                      &nbsp;
                    </option>
                    {model.sysParams.backorderShipViaCodes.map(x => (
                      <option key={x.code} value={x.code}>
                        {x.code} - {x.desc}
                      </option>
                    ))}
                  </select>
                </div>
                {!isRO && (
                  <>
                    <div className="sk-confirmationInput">
                      <Label>Ship Out</Label>
                      <select
                        onChange={evt => updateConfirmationField('shipOut', evt.target.value)}
                        value={bo.confirmationDetail.shipOut}
                      >
                        <option disabled={true} value={blank}>
                          &nbsp;
                        </option>
                        {model.sysParams.shippingCodes.map(x => (
                          <option key={x.code} value={x.code}>
                            {x.code} - {x.desc}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sk-confirmationInputCustomerPO">
                      <Label>
                        Customer PO #{' '}
                        {model.sysParams.showPurchaseOrderPattern && model.customerParams.purchaseOrderPatternReq}
                      </Label>
                      <div>
                        {model.customerParams.purchaseOrderBlanketPO && (
                          <div className="sk-confirmationBlanketPODisplay">
                            {model.customerParams.purchaseOrderBlanketPO}
                          </div>
                        )}
                        <input
                          type="text"
                          onChange={evt => updateConfirmationField('customerPO', evt.target.value)}
                          value={bo.confirmationDetail.customerPO}
                          className={
                            model.customerParams.purchaseOrderBlanketPO ? 'sk-confirmationBlanketPOActive' : ''
                          }
                          onBlur={() => {
                            const ref = customerPoRef.current!;
                            const po = ref.value.trim();
                            if (po !== '' && !model.validatePo(po)) {
                              toastr.error('Error', 'Invalid Customer PO format.', { toastId: 'customerPo' });
                            }
                          }}
                          ref={customerPoRef}
                        />
                      </div>
                    </div>
                  </>
                )}
                {renderConfirmationCharges()}
                <hr />
                <div className="sk-contactInfoRow">
                  <div className="sk-confirmationInput">
                    <Label>Contact Name</Label>
                    <input
                      type="text"
                      onChange={evt => updateConfirmationField('contactName', evt.target.value)}
                      value={bo.confirmationDetail.contactName}
                    />
                  </div>
                  <div className="sk-confirmationInput">
                    <Label>Contact Phone</Label>
                    <input
                      type="text"
                      onChange={evt => updateConfirmationField('contactPhone', evt.target.value)}
                      value={bo.confirmationDetail.contactPhone}
                      onBlur={() => {
                        const ref = customerPhoneRef.current!;
                        const phone = ref.value.trim();
                        if (phone !== '' && !validatePhoneField(phone)) {
                          toastr.error('Error', 'Contact phone requires 10 digits.', { toastId: 'contactPhone' });
                        }
                      }}
                      ref={customerPhoneRef}
                    />
                  </div>
                  <div className="sk-confirmationInput">
                    <Label>Contact Fax</Label>
                    <input
                      type="text"
                      onChange={evt => updateConfirmationField('contactFax', evt.target.value)}
                      value={bo.confirmationDetail.contactFax}
                      onBlur={() => {
                        const ref = customerFaxRef.current!;
                        const fax = ref.value.trim();
                        if (fax !== '' && !validatePhoneField(fax)) {
                          toastr.error('Error', 'Contact fax requires 10 digits.', { toastId: 'contactFax' });
                        }
                      }}
                      ref={customerFaxRef}
                    />
                  </div>
                  <div className="sk-confirmationInput">
                    <Label>Contact E-mail</Label>
                    <input
                      type="text"
                      maxLength={50}
                      onChange={evt => updateConfirmationField('contactEmail', evt.target.value)}
                      value={bo.confirmationDetail.contactEmail}
                    />
                  </div>
                </div>
                <Label>Purchasing Comments</Label>
                <input
                  type="text"
                  style={{ width: '98%' }}
                  maxLength={50}
                  onChange={evt => updateConfirmationField('specialComments', evt.target.value)}
                  value={bo.confirmationDetail.specialComments}
                />
              </div>
            </fieldset>
          </Col>
        </Row>
        <Row>
          <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
            <Col xs={8} style={{ paddingRight: '0' }}>
              <Tab.Container activeKey={`po-${activePO}`} id="sk-poNavigator" onSelect={onPOTabSelect}>
                <Row className="clearfix sk-poNavigator">
                  <Col xs={2} className="sk-poSelector">
                    <span className="sk-fullWidthLabel">
                      <strong>Attached POs</strong>
                    </span>
                    <div className="sk-poSelectorItems">
                      <Nav variant="pills" className="flex-column">
                        {bo.purchaseOrders.map((purchaseOrder, i) => (
                          <Nav.Item key={`po-${i}`}>
                            <Nav.Link eventKey={`po-${i}`}>{mode === 'NOW' ? 'PO' : 'DAILY'}</Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </div>
                  </Col>
                  <Col xs={10} className="sk-poContainer">
                    <Tab.Content>
                      {bo.purchaseOrders.map((purchaseOrder, poIndex) => {
                        let suggestedVendorWarningMessage = false;
                        return (
                          <Tab.Pane key={`po-${poIndex}`} eventKey={`po-${poIndex}`}>
                            <Droppable droppableId={`po-${poIndex}`}>
                              {(providedDroppable, snapshotDroppable) => (
                                <div
                                  ref={providedDroppable.innerRef}
                                  className="sk-poContents"
                                  style={{
                                    backgroundColor: snapshotDroppable.isDraggingOver ? 'lightyellow' : '#f5f5f5',
                                    transition: 'background-color 0.2s ease',
                                  }}
                                  {...providedDroppable.droppableProps}
                                >
                                  {mode === 'NOW' && (
                                    <div className="sk-poInformation">
                                      <div className="sk-poInformationLegend">Purchase Order Details</div>
                                      <div className="sk-poDetailsContainer">
                                        <div id="po-details-control">
                                          <Tabs defaultActiveKey="1">
                                            <Tab eventKey="1" title="Required" tabClassName="po-details-control">
                                              <div className="sk-poDetailsInputFlexContainer">
                                                <div className="sk-poDetailsInput">
                                                  <Label>Suggested Vendor</Label>
                                                  <select
                                                    disabled={bo.purchaseOrders[poIndex].poDetail.vendorSearch !== ''}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'suggestedVendor', evt.target.value)
                                                    }
                                                    value={purchaseOrder.poDetail.suggestedVendor}
                                                    style={{
                                                      backgroundColor:
                                                        bo.purchaseOrders[poIndex].poDetail.vendorSearch !== ''
                                                          ? '#EBEBE4'
                                                          : undefined,
                                                    }}
                                                  >
                                                    <option value={blank}>&nbsp;</option>
                                                    {bo.purchaseOrders[poIndex].parts.length > 0 &&
                                                      renderedDerivedVendorSuggestions[poIndex]}
                                                    {vendorSuggestions['ATB13MISC'].length > 0 && (
                                                      <option value="mv-separator" disabled={true}>
                                                        ---------- MISC VENDORS ----------
                                                      </option>
                                                    )}
                                                    {vendorSuggestions['ATB13MISC'].map((miscVendor, mvIndex) => (
                                                      <option key={`mv${mvIndex}`} value={miscVendor.num}>
                                                        {miscVendor.num}: {miscVendor.name}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Dealer PO</Label>
                                                  <input
                                                    type="text"
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'dealerPO', evt.target.value)
                                                    }
                                                    value={purchaseOrder.poDetail.dealerPO}
                                                  />
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Vendor Search</Label>
                                                  <VendorLookup
                                                    disabled={isProcessing}
                                                    id={`vendorSearchLookup${poIndex}`}
                                                    onVendorLoad={(vendorID: string) =>
                                                      updatePOField(poIndex, 'vendorSearch', vendorID)
                                                    }
                                                    overflowBreakout={true}
                                                    placeholder=""
                                                    showAdditionalNames={model.sysParams.showVendorName23}
                                                    showVendorName={true}
                                                    style={null}
                                                    ref={ref => {
                                                      vendorSearchLookupRefs[poIndex] = ref as VendorLookup;
                                                    }}
                                                  />
                                                  <span className="sk-shipInOverrideNote">
                                                    Overrides Suggested Vendor If Set
                                                  </span>
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Ship In</Label>
                                                  <select
                                                    onChange={evt => updatePOField(poIndex, 'shipIn', evt.target.value)}
                                                    value={purchaseOrder.poDetail.shipIn}
                                                  >
                                                    <option value={blank}>&nbsp;</option>
                                                    {model.sysParams.backorderShipViaCodes.map(x => (
                                                      <option key={x.code} value={x.code}>
                                                        {x.code} - {x.desc}
                                                      </option>
                                                    ))}
                                                  </select>
                                                  <span className="sk-shipInOverrideNote">
                                                    Overrides Confirmation Setting If Different
                                                  </span>
                                                </div>
                                              </div>
                                            </Tab>
                                            <Tab eventKey="2" title="Optional">
                                              <div className="sk-poDetailsInputFlexContainer">
                                                <div className="sk-poDetailsInput">
                                                  <Label>Freight On Board</Label>
                                                  <select
                                                    value={purchaseOrder.poDetail.freightOnBoard}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'freightOnBoard', evt.target.value)
                                                    }
                                                  >
                                                    <option value={blank}>&nbsp;</option>
                                                    <option value="O">O - Origin</option>
                                                    <option value="D">D - Destination</option>
                                                  </select>
                                                </div>
                                                <div className="sk-poDetailsInput">
                                                  <Label>Freight Payment</Label>
                                                  <select
                                                    value={purchaseOrder.poDetail.freightPayment}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'freightPayment', evt.target.value)
                                                    }
                                                  >
                                                    <option value={blank}>&nbsp;</option>
                                                    <option value="P">P - Pre-paid</option>
                                                    <option value="C">C - Collect</option>
                                                  </select>
                                                </div>
                                                <div className="sk-poDetailsInput sk-poInternalComments">
                                                  <Label>Internal PO Comments</Label>
                                                  <input
                                                    type="text"
                                                    maxLength={50}
                                                    onChange={evt =>
                                                      updatePOField(
                                                        poIndex,
                                                        'dealershipInternalComments',
                                                        evt.target.value
                                                      )
                                                    }
                                                    value={purchaseOrder.poDetail.dealershipInternalComments}
                                                  />
                                                </div>
                                              </div>
                                            </Tab>
                                            <Tab eventKey="3" title="Misc Vendor Details">
                                              {isMiscVendorSelected() ? (
                                                <div className="sk-poDetailsInputFlexContainer">
                                                  <div className="sk-poDetailsInput">
                                                    <Label>Vendor Name</Label>
                                                    <input
                                                      type="text"
                                                      value={purchaseOrder.poDetail.miscVendorName}
                                                      onChange={evt =>
                                                        updatePOField(poIndex, 'miscVendorName', evt.target.value)
                                                      }
                                                    />
                                                  </div>
                                                  <div className="sk-poDetailsInput">
                                                    <Label>Address</Label>
                                                    <input
                                                      type="text"
                                                      value={purchaseOrder.poDetail.miscAddress}
                                                      onChange={evt =>
                                                        updatePOField(poIndex, 'miscAddress', evt.target.value)
                                                      }
                                                    />
                                                  </div>
                                                  <div className="sk-poDetailsInput">
                                                    <Label>Attention</Label>
                                                    <input
                                                      type="text"
                                                      value={purchaseOrder.poDetail.miscAttention}
                                                      onChange={evt =>
                                                        updatePOField(poIndex, 'miscAttention', evt.target.value)
                                                      }
                                                    />
                                                  </div>
                                                  <div className="sk-poDetailsInput">
                                                    <Label>City, State Zip</Label>
                                                    <input
                                                      type="text"
                                                      value={purchaseOrder.poDetail.miscCityStateZip}
                                                      onChange={evt =>
                                                        updatePOField(poIndex, 'miscCityStateZip', evt.target.value)
                                                      }
                                                    />
                                                  </div>
                                                  <div className="sk-poDetailsInput">
                                                    <Label>Phone</Label>
                                                    <input
                                                      type="text"
                                                      value={purchaseOrder.poDetail.miscPhone}
                                                      onChange={evt =>
                                                        updatePOField(poIndex, 'miscPhone', evt.target.value)
                                                      }
                                                      onBlur={evt => {
                                                        if (!validatePhoneField(evt.target.value)) {
                                                          toastr.error(
                                                            'Error',
                                                            `PO ${poIndex + 1}: Misc vendor phone requires 10 digits.`,
                                                            { toastId: `PO${poIndex}MiscVendorPhone` }
                                                          );
                                                        }
                                                      }}
                                                    />
                                                  </div>
                                                  <div className="sk-poDetailsInput">&nbsp;</div>
                                                </div>
                                              ) : (
                                                <div className="sk-miscVendorNotSelected">
                                                  Miscellaneous Vendor Not Selected
                                                </div>
                                              )}
                                            </Tab>
                                            <Tab eventKey="4" title="Direct Ship To">
                                              <div className="sk-poDetailsInputFlexContainer sk-poDirectShipToFirstRow">
                                                <div className="sk-poDetailsShipToInput">
                                                  <input
                                                    type="radio"
                                                    id={`shiptodealership${poIndex}`}
                                                    name={`directshipto${poIndex}`}
                                                    value="dealership"
                                                    checked={purchaseOrder.poDetail.directShipType === 'D'}
                                                    onChange={evt =>
                                                      evt.target.checked &&
                                                      updatePOField(poIndex, 'directShipType', 'D')
                                                    }
                                                  />
                                                  <label htmlFor={`shiptodealership${poIndex}`}>
                                                    <Label>Dealership</Label>
                                                  </label>
                                                  <br />
                                                  <input type="text" disabled={true} placeholder="No Override" />
                                                </div>

                                                <div className="sk-poDetailsShipToInput">
                                                  <input
                                                    type="radio"
                                                    id={`shiptocustomer${poIndex}`}
                                                    name={`directshipto${poIndex}`}
                                                    value="customer"
                                                    checked={purchaseOrder.poDetail.directShipType === 'C'}
                                                    onChange={evt =>
                                                      evt.target.checked &&
                                                      updatePOField(poIndex, 'directShipType', 'C')
                                                    }
                                                  />
                                                  <label htmlFor={`shiptocustomer${poIndex}`}>
                                                    <Label>Customer #</Label>
                                                  </label>
                                                  <br />
                                                  <CustomerLookup
                                                    activeId={null}
                                                    disabled={
                                                      purchaseOrder.poDetail.directShipType !== 'C' || isProcessing
                                                    }
                                                    hideInactiveAccounts={model.sysParams.hideInactiveAccounts}
                                                    id={`directShipCustomerLookup${poIndex}`}
                                                    onCustomerLoad={(customerID: string) =>
                                                      updatePOField(poIndex, 'directShipCustomerNum', customerID)
                                                    }
                                                    onEnterKeyPressed={() => {
                                                      /* */
                                                    }}
                                                    onPickListLoad={() => {
                                                      console.error(
                                                        'Legacy pick list loads not supported in backshipping modal.'
                                                      );
                                                    }}
                                                    overflowBreakout={true}
                                                    placeholder=""
                                                    showAdditionalNames={model.sysParams.showName23}
                                                    skipActiveIDCheck={true}
                                                    style={null}
                                                    ref={ref => {
                                                      directShipCustomerLookupRefs[poIndex] = ref as CustomerLookup;
                                                    }}
                                                  />
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <input
                                                    type="radio"
                                                    id={`shiptovendor${poIndex}`}
                                                    name={`directshipto${poIndex}`}
                                                    value="vendor"
                                                    checked={purchaseOrder.poDetail.directShipType === 'V'}
                                                    onChange={evt =>
                                                      evt.target.checked &&
                                                      updatePOField(poIndex, 'directShipType', 'V')
                                                    }
                                                  />
                                                  <label htmlFor={`shiptovendor${poIndex}`}>
                                                    <Label>Vendor #</Label>
                                                  </label>
                                                  <br />
                                                  <VendorLookup
                                                    disabled={
                                                      purchaseOrder.poDetail.directShipType !== 'V' || isProcessing
                                                    }
                                                    id={`directShipVendorLookup${poIndex}`}
                                                    onVendorLoad={(vendorID: string) =>
                                                      updatePOField(poIndex, 'directShipVendorNum', vendorID)
                                                    }
                                                    overflowBreakout={true}
                                                    placeholder=""
                                                    showAdditionalNames={model.sysParams.showVendorName23}
                                                    style={null}
                                                    ref={ref => {
                                                      directShipVendorLookupRefs[poIndex] = ref as VendorLookup;
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                              <hr />
                                              <div className="sk-poDetailsInputFlexContainer sk-poDirectShipToOtherRows">
                                                <div className="sk-poDetailsShipToInput">
                                                  <input
                                                    type="radio"
                                                    id={`shiptoother${poIndex}`}
                                                    name={`directshipto${poIndex}`}
                                                    value="other"
                                                    checked={purchaseOrder.poDetail.directShipType === 'O'}
                                                    onChange={evt =>
                                                      evt.target.checked &&
                                                      updatePOField(poIndex, 'directShipType', 'O')
                                                    }
                                                  />
                                                  <label htmlFor={`shiptoother${poIndex}`}>
                                                    <Label>
                                                      <span className="sk-poDirectShipToOtherLabel">Other</span>
                                                    </Label>
                                                  </label>
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <Label>Name</Label>
                                                  <input
                                                    type="text"
                                                    disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                    value={purchaseOrder.poDetail.directShipOtherName}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'directShipOtherName', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <Label>Attention</Label>
                                                  <input
                                                    type="text"
                                                    disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                    value={purchaseOrder.poDetail.directShipOtherAttention}
                                                    onChange={evt =>
                                                      updatePOField(
                                                        poIndex,
                                                        'directShipOtherAttention',
                                                        evt.target.value
                                                      )
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <Label>Address</Label>
                                                  <input
                                                    type="text"
                                                    disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                    value={purchaseOrder.poDetail.directShipOtherAddress}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'directShipOtherAddress', evt.target.value)
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <Label>City, State Zip</Label>
                                                  <input
                                                    type="text"
                                                    disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                    value={purchaseOrder.poDetail.directShipOtherCityStateZip}
                                                    onChange={evt =>
                                                      updatePOField(
                                                        poIndex,
                                                        'directShipOtherCityStateZip',
                                                        evt.target.value
                                                      )
                                                    }
                                                  />
                                                </div>
                                                <div className="sk-poDetailsShipToInput">
                                                  <Label>Phone</Label>
                                                  <input
                                                    type="text"
                                                    disabled={purchaseOrder.poDetail.directShipType !== 'O'}
                                                    value={purchaseOrder.poDetail.directShipOtherPhone}
                                                    onChange={evt =>
                                                      updatePOField(poIndex, 'directShipOtherPhone', evt.target.value)
                                                    }
                                                    onBlur={evt => {
                                                      if (!validatePhoneField(evt.target.value)) {
                                                        toastr.error(
                                                          'Error',
                                                          `PO ${
                                                            poIndex + 1
                                                          }: Direct ship to "other" phone requires 10 digits.`,
                                                          { toastId: `PO${poIndex}DirectShipPhone` }
                                                        );
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              </div>
                                            </Tab>
                                          </Tabs>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {purchaseOrder.parts.length === 0 ? (
                                    <div className="sk-poPartsEmpty">
                                      {mode === 'NOW'
                                        ? 'No parts attached to this PO.'
                                        : 'Add parts to the daily order.'}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="sk-poParts">
                                        <table className="sk-poPartLines">
                                          <thead>
                                            <tr>
                                              <td title="Part Number">Part</td>
                                              <td title="Stock Class">Stkcls</td>
                                              <td title="Requested Quantity">Req</td>
                                              <td title="On Hand (Inventory)">OH</td>
                                              <td title="Action">Action</td>
                                              <td title="Prebill">{isRO ? '' : 'Prebill'}</td>
                                              <td>&nbsp;</td>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {purchaseOrder.parts.map((part, partLineIndex) => {
                                              const isPoComment = 'poComment' in part;
                                              const partDetails = parts[part.partIndex] as ICandidatePartDetail;
                                              const suggestedVendor = purchaseOrder.poDetail.suggestedVendor;
                                              let suggestedVendorStockClassMistmatch = false;

                                              // Suggested vendor could be a miscellaneous vendor, which supports all stock classes
                                              if (
                                                !isPoComment &&
                                                purchaseOrder.poDetail.vendorSearch.trim() === '' &&
                                                derivedVendorSuggestions[poIndex] &&
                                                suggestedVendor in derivedVendorSuggestions[poIndex]
                                              ) {
                                                if (
                                                  !derivedVendorSuggestions[poIndex][suggestedVendor].stockClasses.has(
                                                    partDetails.stockClass
                                                  )
                                                ) {
                                                  suggestedVendorStockClassMistmatch = true;
                                                  suggestedVendorWarningMessage = true;
                                                }
                                              }

                                              const PartContextMenu = ({
                                                id,
                                                lookupKey,
                                              }: {
                                                id: string;
                                                lookupKey: string;
                                              }) => {
                                                return (
                                                  <Menu id={id} animation="fade" style={{ zIndex: 5000 }}>
                                                    <Item
                                                      onClick={() => {
                                                        modal.open('partDetail', { lookupKey });
                                                      }}
                                                    >
                                                      Part Detail
                                                    </Item>
                                                    <Item
                                                      onClick={() => {
                                                        modal.open('partHistory', { lookupKey });
                                                      }}
                                                    >
                                                      Part History
                                                    </Item>
                                                    <Item
                                                      onClick={() => {
                                                        modal.open('attachments', {
                                                          itemId: lookupKey,
                                                          itemName: 'PIV1',
                                                        });
                                                      }}
                                                    >
                                                      View Attachments
                                                    </Item>
                                                  </Menu>
                                                );
                                              };

                                              return (
                                                <Draggable
                                                  draggableId={part.partIndex}
                                                  key={part.partIndex}
                                                  index={partLineIndex}
                                                >
                                                  {(providedDraggable, snapshotDraggable) => {
                                                    let child = (
                                                      <tr
                                                        className={classNames({
                                                          'sk-noBgHighlight': dragState.isDragging,
                                                          'sk-suggestedVendorStockClassMismatch':
                                                            suggestedVendorStockClassMistmatch,
                                                        })}
                                                        {...providedDraggable.draggableProps}
                                                      >
                                                        {isPoComment ? (
                                                          part.poCommentIsEditing ? (
                                                            <td
                                                              colSpan={6}
                                                              ref={providedDraggable.innerRef}
                                                              {...providedDraggable.dragHandleProps}
                                                            >
                                                              <input
                                                                defaultValue={part.poComment}
                                                                onBlur={() =>
                                                                  updatePartLineComment({
                                                                    partLineIndex,
                                                                    poIndex: activePO,
                                                                  })
                                                                }
                                                                onKeyPress={evt => {
                                                                  if (evt.key === 'Enter') {
                                                                    updatePartLineComment({
                                                                      partLineIndex,
                                                                      poIndex: activePO,
                                                                    });
                                                                  }
                                                                }}
                                                                ref={poCommentEditInputRef}
                                                                style={{ lineHeight: 1, width: '100%' }}
                                                                type="text"
                                                              />
                                                            </td>
                                                          ) : (
                                                            <td
                                                              colSpan={6}
                                                              className="sk-poCommentLine"
                                                              onDoubleClick={() => {
                                                                if (isProcessing) {
                                                                  return;
                                                                }
                                                                setCommentIsEditing({
                                                                  partLineIndex,
                                                                  poIndex: activePO,
                                                                });
                                                              }}
                                                            >
                                                              <div
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.dragHandleProps}
                                                                className="sk-clipper"
                                                              >
                                                                {part.poComment}
                                                              </div>
                                                            </td>
                                                          )
                                                        ) : (
                                                          <>
                                                            <td title={`${partDetails.partNum} - ${partDetails.desc}`}>
                                                              <div
                                                                ref={providedDraggable.innerRef}
                                                                {...providedDraggable.dragHandleProps}
                                                              >
                                                                <MenuProvider
                                                                  event={contextMenuEvent}
                                                                  id={`${poIndex}-${partLineIndex}`}
                                                                >
                                                                  <div className="sk-clipper">
                                                                    {partDetails.partNum}
                                                                  </div>
                                                                </MenuProvider>
                                                                <PartContextMenu
                                                                  id={`${poIndex}-${partLineIndex}`}
                                                                  lookupKey={partDetails.partNum}
                                                                />
                                                              </div>
                                                            </td>
                                                            <td title={partDetails.stockClass}>
                                                              <div className="sk-clipper">{partDetails.stockClass}</div>
                                                            </td>
                                                            <td>
                                                              <div className="sk-clipper">{partDetails.req}</div>
                                                            </td>
                                                            <td>
                                                              <div className="sk-clipper">{partDetails.oh}</div>
                                                            </td>
                                                            <td>
                                                              <i
                                                                className={classNames('zmdi zmdi-circle', {
                                                                  'sk-holdOnHand': part.boAction === 'HOLD_ON_HAND',
                                                                  'sk-invoiceOnHand':
                                                                    part.boAction === 'INVOICE_ON_HAND',
                                                                  'sk-orderFullQty':
                                                                    part.boAction === 'ORDER_FULL_QUANTITY',
                                                                })}
                                                              />
                                                              &nbsp;
                                                              <select
                                                                disabled={isProcessing}
                                                                defaultValue={part.boAction}
                                                                onChange={evt =>
                                                                  changePartLineActions({
                                                                    action: evt.target.value as POPartLineAction,
                                                                    partLineIndex,
                                                                    poIndex,
                                                                  })
                                                                }
                                                              >
                                                                {partDetails.oh > 0 && !partDetails.sufficientQty && (
                                                                  <option value="INVOICE_ON_HAND">
                                                                    {isRO ? 'Issue' : 'Invoice'} On-Hand &nbsp;
                                                                  </option>
                                                                )}
                                                                <option value="ORDER_FULL_QUANTITY">
                                                                  Order Full Qty
                                                                </option>
                                                                {!model.sysParams.noHeldOrders &&
                                                                  partDetails.oh > 0 && (
                                                                    <option value="HOLD_ON_HAND">Hold On-Hand</option>
                                                                  )}
                                                              </select>
                                                            </td>
                                                            <td>
                                                              {part.boAction !== 'HOLD_ON_HAND' && !isRO && (
                                                                <FormCheck inline={true}>
                                                                  <FormCheck.Input
                                                                    type="checkbox"
                                                                    className="sk-useMaterialInput"
                                                                    onChange={(evt: any) => {
                                                                      changePartLineActions({
                                                                        partLineIndex,
                                                                        poIndex,
                                                                        prebill: evt.target.checked,
                                                                      });
                                                                    }}
                                                                    checked={part.prebill}
                                                                  />
                                                                </FormCheck>
                                                              )}
                                                            </td>
                                                          </>
                                                        )}
                                                        <td>
                                                          <i
                                                            className="zmdi zmdi-delete zmdi-hc-lg sk-poRemoveItemIcon"
                                                            onClick={() => removePartLine({ partLineIndex, poIndex })}
                                                          />
                                                        </td>
                                                      </tr>
                                                    );

                                                    if (snapshotDraggable.isDragging) {
                                                      const content = isPoComment ? (
                                                        part.poComment
                                                      ) : (
                                                        <>
                                                          {partDetails.partNum}
                                                          <br />
                                                          <span style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                                            {partDetails.stockClass &&
                                                              `(STK:${partDetails.stockClass}) `}
                                                            {partDetails.desc}
                                                          </span>
                                                        </>
                                                      );

                                                      child = (
                                                        <div
                                                          ref={providedDraggable.innerRef}
                                                          {...providedDraggable.draggableProps}
                                                          {...providedDraggable.dragHandleProps}
                                                          style={getDraggableStyle(
                                                            providedDraggable.draggableProps.style,
                                                            snapshotDraggable,
                                                            bo.purchaseOrders[activePO].parts.length === 0
                                                          )}
                                                        >
                                                          {/* Inline styles required in all parts of this DOM node tree since it will be rendered in a portal. */}
                                                          <div
                                                            style={{
                                                              backgroundColor: '#e2f1fd',
                                                              border: '1px solid #2196f3',
                                                              fontStyle: isPoComment ? 'italic' : 'normal',
                                                              fontWeight: isPoComment ? 'normal' : 'bold',
                                                              lineHeight: '1.1',
                                                              overflowX: 'hidden',
                                                              padding: '5px',
                                                              textOverflow: 'ellipsis',
                                                              whiteSpace: 'nowrap',
                                                              width: '235px',
                                                            }}
                                                          >
                                                            {content}
                                                          </div>
                                                        </div>
                                                      );

                                                      return ReactDOM.createPortal(child, portal);
                                                    } else {
                                                      return child;
                                                    }
                                                  }}
                                                </Draggable>
                                              );
                                            })}
                                            <tr>
                                              <td colSpan={7}>{providedDroppable.placeholder}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      {suggestedVendorWarningMessage && (
                                        <div className="sk-suggestedVendorWarningMessage">
                                          The suggested vendor chosen does not support all stock classes on this PO.
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </Tab.Pane>
                        );
                      })}
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </Col>
            <Col xs={4} style={{ paddingLeft: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="sk-fullWidthLabel">
                  <strong>Candidate Backorder Parts</strong>
                </span>
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    id="sk-show-all-parts"
                    className="sk-useMaterialInput ml-1"
                    onChange={(evt: any) => handleShowAllParts(evt.target.checked)}
                    checked={showAllParts}
                  />
                  <FormCheck.Label htmlFor="sk-show-all-parts">
                    Show All
                    <OverlayTrigger placement="bottom" overlay={showAllPopover}>
                      <span className="sk-showAllPartsHelp ml-1">
                        <i className="zmdi zmdi-help" />
                      </span>
                    </OverlayTrigger>
                  </FormCheck.Label>
                </FormCheck>
              </div>
              <Droppable droppableId="partList">
                {(providedDroppable, snapshotDroppable) => (
                  <div
                    ref={providedDroppable.innerRef}
                    className="sk-partSelector"
                    style={{
                      backgroundColor: snapshotDroppable.isDraggingOver ? 'lightyellow' : '#f5f5f5',
                      transition: 'background-color 0.2s ease',
                    }}
                    {...providedDroppable.droppableProps}
                  >
                    {visibleCandidatePartListLength === 0 ? (
                      <div style={{ textAlign: 'center' }}>Empty</div>
                    ) : (
                      <>
                        {(() => {
                          /* react-beautiful-dnd requires sequential indexes, but in our candidate list scheme,
                             some parts might be skipped (sufficient qty with show parts unchecked).
                             Proper transposition (of the candiate list) occurs in onDragEnd().
                          */
                          let renderIndex = 0;
                          return candidatePartList.map(candidatePartIndex => {
                            const partDetails = parts[candidatePartIndex] as ICandidatePartDetail;

                            if (partDetails.sufficientQty && !showAllParts) {
                              return null;
                            }

                            return (
                              <Draggable
                                draggableId={candidatePartIndex}
                                index={renderIndex++}
                                key={`draggable-${candidatePartIndex}`}
                              >
                                {(providedDraggable, snapshotDraggable) => {
                                  let backgroundColor, border, color;

                                  // Blue parts = candidate backorder parts
                                  // Orange parts = parts with sufficient qty on hand to meet demand ("Show All" feature)

                                  // Is this item selected in a multi-part drag?
                                  if (partSelectionToggles[candidatePartIndex]) {
                                    // If we're currently dragging the multi-part set and NOT the primary drag item (gray out).
                                    if (dragState.isDragging && dragState.id !== candidatePartIndex) {
                                      backgroundColor = '#f5f5f5';
                                      color = '#c2c2c2';
                                      border = '1px solid #c2c2c2';
                                      // Otherwise indicate we are part of a multi-part drag set (darker blue).
                                    } else {
                                      backgroundColor = '#b2dbfb';
                                      border = '1px solid #2196f3';
                                    }
                                    // Not selected in multi-part drag (standard light blue / light orange).
                                  } else {
                                    if (partDetails.partNum === 'LINEITEM_COMMENT') {
                                      backgroundColor = '#f9ecf6';
                                      border = '1px solid #a03588';
                                    } else if (partDetails.sufficientQty) {
                                      backgroundColor = '#ffe8cc';
                                      border = '1px solid #ff8c00';
                                    } else {
                                      backgroundColor = '#e2f1fd';
                                      border = '1px solid #2196f3';
                                    }
                                  }

                                  const child = (
                                    <div
                                      className="sk-partSelectorItem"
                                      onClick={event => onPartClick(event, candidatePartIndex)}
                                      ref={providedDraggable.innerRef}
                                      {...providedDraggable.draggableProps}
                                      {...providedDraggable.dragHandleProps}
                                      style={getDraggableStyle(
                                        providedDraggable.draggableProps.style,
                                        snapshotDraggable,
                                        bo.purchaseOrders[activePO].parts.length === 0
                                      )}
                                    >
                                      {/* Inline styles required in all parts of this DOM node tree since it will be rendered in a portal. */}
                                      <div
                                        style={{
                                          backgroundColor,
                                          border,
                                          color,
                                          fontWeight: 'bold',
                                          lineHeight: '1.1',
                                          overflowX: 'hidden',
                                          padding: '5px',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {partDetails.partNum === 'LINEITEM_COMMENT'
                                          ? 'Memo / Comment'
                                          : partDetails.partNum}
                                        <br />
                                        <span style={{ fontSize: '10px', fontWeight: 'normal' }}>
                                          {partDetails.stockClass && `(STK:${partDetails.stockClass}) `}
                                          {partDetails.desc}
                                        </span>
                                        {snapshotDraggable.isDragging && selectedPartCount > 1 && (
                                          <div
                                            style={{
                                              alignItems: 'center',
                                              backgroundColor: '#2196f3',
                                              borderRadius: '50%',
                                              color: 'white',
                                              display: 'flex',
                                              fontSize: '12px',
                                              height: '28px',
                                              justifyContent: 'center',
                                              position: 'absolute',
                                              right: '-13px',
                                              top: '-13px',
                                              width: '28px',
                                            }}
                                          >
                                            {selectedPartCount}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );

                                  if (snapshotDraggable.isDragging) {
                                    return ReactDOM.createPortal(child, portal);
                                  } else {
                                    return child;
                                  }
                                }}
                              </Draggable>
                            );
                          });
                        })()}
                        {providedDroppable.placeholder}
                      </>
                    )}
                  </div>
                )}
              </Droppable>
              {visibleCandidatePartListLength !== 0 && (
                <div className="sk-partListActions">
                  <Button size="sm" onClick={addAllPartsToPO} variant="secondary">
                    ADD REMAINING
                  </Button>
                  <Button size="sm" onClick={clearPartSelection} variant="secondary">
                    CLEAR SELECTION
                  </Button>
                </div>
              )}
              <div className="sk-poComment">
                <input
                  type="text"
                  placeholder="Comment (Press ENTER to add to PO)"
                  ref={poCommentInputRef}
                  onKeyPress={evt => {
                    if (evt.key === 'Enter' && poCommentInputRef.current!.value.trim().length) {
                      addPOComment();
                    }
                  }}
                />
              </div>
            </Col>
          </DragDropContext>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Dropdown drop="up" as={ButtonGroup}>
              <Button variant="warning" disabled={mode === 'DAILY'} id="split-button-dropup" onClick={addPO}>
                ADD PO
              </Button>
              <Dropdown.Toggle split={true} variant="warning" id="split-button-dropup" disabled={mode === 'DAILY'} />
              <Dropdown.Menu>
                <Dropdown.Item onClick={deletePO}>Delete Selected PO</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <div className="sk-defaultPOActions">
              <span className="sk-defaultActionLabel">
                <OverlayTrigger placement="top" overlay={defaultActionsPopover}>
                  <span className="sk-defaultActionsHelp">
                    <i className="zmdi zmdi-help" />
                  </span>
                </OverlayTrigger>
                Default Actions :
              </span>

              <i
                className={classNames('zmdi zmdi-circle', {
                  'sk-holdOnHand': defaultBOAction === 'HOLD_ON_HAND',
                  'sk-invoiceOnHand': defaultBOAction === 'INVOICE_ON_HAND',
                  'sk-orderFullQty': defaultBOAction === 'ORDER_FULL_QUANTITY',
                })}
                style={{ marginLeft: '10px' }}
              />
              <select
                defaultValue={defaultBOAction}
                onChange={evt => setDefaultBOAction(evt.target.value as POPartLineAction)}
              >
                <option value="INVOICE_ON_HAND">{isRO ? 'Issue' : 'Invoice'} On-Hand</option>
                <option value="ORDER_FULL_QUANTITY">Order Full Qty</option>
                {!model.sysParams.noHeldOrders && <option value="HOLD_ON_HAND">Hold On-Hand</option>}
              </select>
              {!isRO && (
                <FormCheck inline={true}>
                  <FormCheck.Input
                    type="checkbox"
                    id="sk-prebill"
                    className="sk-useMaterialInput ml-1"
                    onChange={(evt: any) => setDefaultPrebill(evt.target.checked)}
                    checked={defaultPrebill}
                  />
                  <FormCheck.Label htmlFor="sk-prebill">Prebill</FormCheck.Label>
                </FormCheck>
              )}
            </div>
          </div>
          <div>
            <Button disabled={isProcessing} onClick={hide} variant="secondary">
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                if (!isProcessing) {
                  if (validateConfirmation()) {
                    setIsProcessing(true);
                    process();
                  }
                }
              }}
            >
              {isProcessing ? (
                <>
                  <ClipLoader size={13} color="white" /> PROCESSING
                </>
              ) : (
                'PROCESS'
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default BackorderModal;
